import React, { useEffect, useRef, useState } from 'react'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import { makeIcon } from '../OracleCommons/icons'
import { OracleModal } from './OracleModal'

interface TarotReframeModalProps {
  isOpen: boolean
  originalQuestion: string
  suggestions: string[]
  activeSuggestionIndex: number
  onClose: () => void
  onRefresh: () => void
  onAccept: (question: string, wasEdited: boolean) => void
}

export const TarotReframeModal: React.FC<TarotReframeModalProps> = ({
  isOpen,
  originalQuestion,
  suggestions,
  activeSuggestionIndex,
  onClose,
  onRefresh,
  onAccept,
}) => {
  const activeSuggestion = suggestions[activeSuggestionIndex] ?? ''
  const [editValue, setEditValue] = useState(activeSuggestion)
  const suggestionRef = useRef<HTMLTextAreaElement>(null)

  const title = useTranslatedText({ keys: ['tarotReframeModal', 'title'] })
  const closeAriaLabel = useTranslatedText({ keys: ['tarotReframeModal', 'closeAriaLabel'] })
  const editAriaLabel = useTranslatedText({ keys: ['tarotReframeModal', 'editAriaLabel'] })
  const refreshAriaLabel = useTranslatedText({ keys: ['tarotReframeModal', 'refreshAriaLabel'] })
  const useThisQuestion = useTranslatedText({ keys: ['tarotReframeModal', 'useThisQuestion'] })
  const youAsked = useTranslatedText({ keys: ['tarotReframeModal', 'youAsked'] })
  const tryInstead = useTranslatedText({ keys: ['tarotReframeModal', 'tryInstead'] })

  // Reset edit value when the active suggestion changes (refresh) or when the modal closes/reopens
  useEffect(() => {
    setEditValue(activeSuggestion)
  }, [activeSuggestion, isOpen])

  const handleAccept = () => {
    onAccept(editValue, editValue !== activeSuggestion)
  }

  return (
    <OracleModal
      isOpen={isOpen}
      onDismiss={onClose}
      dismissOnTapOut
      className="tarot-reframe-modal"
    >
      <div className="tarot-reframe-modal__header">
        <h2 className="tarot-reframe-modal__title">{title}</h2>
        <button
          type="button"
          className="tarot-reframe-modal__close-btn"
          aria-label={closeAriaLabel}
          onClick={() => onClose()}
        >
          {makeIcon('close')}
        </button>
      </div>
      <div className="tarot-reframe-modal__you-asked">
        <p className="tarot-reframe-modal__section-label">{youAsked}</p>
        <p className="tarot-reframe-modal__original-question">{originalQuestion}</p>
      </div>
      <div className="tarot-reframe-modal__try-instead">
        <div className="tarot-reframe-modal__try-instead-header">
          <p className="tarot-reframe-modal__section-label">{tryInstead}</p>
          <div className="tarot-reframe-modal__try-instead-actions">
            <button
              type="button"
              className="tarot-reframe-modal__edit-btn"
              aria-label={editAriaLabel}
              onClick={() => suggestionRef.current?.focus()}
            >
              {makeIcon('pencil')}
            </button>
            <button
              type="button"
              className="tarot-reframe-modal__refresh-btn"
              aria-label={refreshAriaLabel}
              onClick={() => onRefresh()}
            >
              {makeIcon('reroll')}
            </button>
          </div>
        </div>
        <textarea
          ref={suggestionRef}
          className="tarot-reframe-modal__suggestion-textarea"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
        />
      </div>
      <button
        type="button"
        className="tarot-reframe-modal__accept-btn"
        onClick={handleAccept}
      >
        {useThisQuestion}
      </button>
    </OracleModal>
  )
}
